// ============================================================
// CONDITION ELIGIBILITY - Pure functions for condition
// management UI: categorization, body-type filtering,
// immunity annotation, and reactive trait cleanup.
// Zero React dependency.
// ============================================================
import { COND_NAMES, decodeCond } from './conditions';
import { getBodyConfig } from './bodyConfig';
import type { BodyTypeId } from '../types';
import { isObject, objectTakes, type BodyTyped } from './bodyShape';

// ── Types ─────────────────────────────────────────────────────

export type ConditionCategory = 'fatal' | 'debilitating' | 'status' | 'location' | 'vehicle';

export interface ConditionEntry {
  /** Base condition code (e.g. 'S', 'Cr', 'Immob'). */
  code: string;
  /** Human-readable name from COND_NAMES. */
  name: string;
  /** Severity category for display grouping. */
  category: ConditionCategory;
  /** Whether this condition supports duration (S3, B2, Dy1, etc.). */
  hasDuration: boolean;
  /**
   * What the numeric suffix means: 'round' (a countdown, the default) or
   * 'level' (a magnitude - Fatigue). Drives the Add-Condition picker label.
   */
  // 'intensity' (v8.357.0): Hallucinating's 1-10 - a magnitude with its own decay, not a clock.
  durationUnit?: 'round' | 'level' | 'severity' | 'intensity';
  /** Whether this condition requires a location suffix (Cr:Head, De:Left Arm). */
  requiresLocation: boolean;
  /** Whether this condition is vehicle-only. */
  vehicleOnly: boolean;
  /**
   * What only a living body can suffer - never offered to a vehicle (ruling
   * 2026-10-07, plan map-display B): it bleeds no blood, does not die by
   * degrees, sees no visions and has no voice to lose. Everything else stays,
   * with its beat on the vehicle figure.
   */
  bodyOnly?: true;
}

export interface EligibleCondition extends ConditionEntry {
  /** Whether the target is immune to this condition due to traits. */
  immune: boolean;
  /** Human-readable names of traits granting immunity. */
  immuneSources: string[];
}

export interface ImmunitySource {
  /** Target field name (e.g. 'fromBeyond', 'machineAP'). */
  field: string;
  /** Human-readable trait label. */
  label: string;
  /** Condition base codes this trait grants immunity to. */
  conditions: string[];
  /** Whether the field is numeric (check >0) vs boolean. */
  isNumeric: boolean;
}

export interface TraitCleanupResult {
  /** Conditions array with immune conditions removed. */
  cleaned: string[];
  /** Decoded human-readable names of removed conditions (for flash notification). */
  removed: string[];
  /** Human-readable trait name (for flash notification). */
  traitLabel: string;
}

export interface LocationOption {
  /** Location ID (e.g. 'head', 'hull'). */
  id: string;
  /** Human-readable location name (e.g. 'Head', 'Hull'). */
  label: string;
}

// ── Condition Catalog ─────────────────────────────────────────

/** Categorized registry of all condition types, grouped by severity. */
export const CONDITION_CATALOG: ConditionEntry[] = [
  // Fatal
  { code: 'K', name: COND_NAMES.K, category: 'fatal', hasDuration: false, requiresLocation: false, vehicleOnly: false },
  {
    code: 'Dy',
    name: COND_NAMES.Dy,
    category: 'fatal',
    hasDuration: true,
    requiresLocation: false,
    vehicleOnly: false,
    bodyOnly: true,
  },
  { code: 'U', name: COND_NAMES.U, category: 'fatal', hasDuration: false, requiresLocation: false, vehicleOnly: false },
  {
    code: 'Explodes',
    name: COND_NAMES.Explodes,
    category: 'fatal',
    hasDuration: false,
    requiresLocation: false,
    vehicleOnly: true,
  },

  // Debilitating
  {
    code: 'S',
    name: COND_NAMES.S,
    category: 'debilitating',
    hasDuration: true,
    requiresLocation: false,
    vehicleOnly: false,
  },
  {
    code: 'B',
    name: COND_NAMES.B,
    category: 'debilitating',
    hasDuration: true,
    requiresLocation: false,
    vehicleOnly: false,
  },
  {
    code: 'D',
    name: COND_NAMES.D,
    category: 'debilitating',
    hasDuration: true,
    requiresLocation: false,
    vehicleOnly: false,
  },
  {
    code: 'Pa',
    name: COND_NAMES.Pa,
    category: 'debilitating',
    hasDuration: false,
    requiresLocation: false,
    vehicleOnly: false,
  },
  {
    code: 'NS',
    name: COND_NAMES.NS,
    category: 'debilitating',
    hasDuration: false,
    requiresLocation: false,
    vehicleOnly: false,
    bodyOnly: true,
  },

  // Status
  {
    code: 'P',
    name: COND_NAMES.P,
    category: 'status',
    hasDuration: false,
    requiresLocation: false,
    vehicleOnly: false,
  },
  // Prepared states (v8.214.0) - normally written by the action drum,
  // listed here so a GM can grant or correct one by hand. Event-expired,
  // so none of them takes a duration.
  {
    code: 'Ah',
    name: COND_NAMES.Ah,
    category: 'status',
    hasDuration: false,
    requiresLocation: false,
    vehicleOnly: false,
  },
  {
    code: 'Af',
    name: COND_NAMES.Af,
    category: 'status',
    hasDuration: false,
    requiresLocation: false,
    vehicleOnly: false,
  },
  {
    code: 'Ft',
    name: COND_NAMES.Ft,
    category: 'status',
    hasDuration: false,
    requiresLocation: false,
    vehicleOnly: false,
  },
  {
    code: 'Ds',
    name: COND_NAMES.Ds,
    category: 'status',
    hasDuration: false,
    requiresLocation: false,
    vehicleOnly: false,
  },
  {
    code: 'Rn',
    name: COND_NAMES.Rn,
    category: 'status',
    hasDuration: false,
    requiresLocation: false,
    vehicleOnly: false,
  },
  {
    code: 'Ta',
    name: COND_NAMES.Ta,
    category: 'status',
    hasDuration: false,
    requiresLocation: false,
    vehicleOnly: false,
  },
  {
    code: 'Dg',
    name: COND_NAMES.Dg,
    category: 'status',
    hasDuration: false,
    requiresLocation: false,
    vehicleOnly: false,
  },
  {
    code: 'Gd',
    name: COND_NAMES.Gd,
    category: 'status',
    hasDuration: false,
    requiresLocation: false,
    vehicleOnly: false,
  },
  {
    code: 'Ao',
    name: COND_NAMES.Ao,
    category: 'status',
    hasDuration: false,
    requiresLocation: false,
    vehicleOnly: false,
  },
  {
    code: 'Ov',
    name: COND_NAMES.Ov,
    category: 'status',
    hasDuration: false,
    requiresLocation: false,
    vehicleOnly: false,
  },
  {
    code: 'Unw',
    name: COND_NAMES.Unw,
    category: 'status',
    hasDuration: false,
    requiresLocation: false,
    vehicleOnly: false,
  },
  {
    code: 'Hpl',
    name: COND_NAMES.Hpl,
    category: 'status',
    hasDuration: false,
    requiresLocation: false,
    vehicleOnly: false,
  },
  {
    code: 'F',
    name: COND_NAMES.F,
    category: 'status',
    hasDuration: true,
    durationUnit: 'level',
    requiresLocation: false,
    vehicleOnly: false,
  },
  {
    code: 'BL',
    name: COND_NAMES.BL,
    category: 'status',
    hasDuration: false,
    requiresLocation: false,
    vehicleOnly: false,
    bodyOnly: true,
  },
  {
    code: 'BLS',
    name: COND_NAMES.BLS,
    category: 'status',
    hasDuration: false,
    requiresLocation: false,
    vehicleOnly: false,
    bodyOnly: true,
  },
  {
    code: 'Fi',
    name: COND_NAMES.Fi,
    category: 'status',
    hasDuration: false,
    requiresLocation: false,
    vehicleOnly: false,
  },
  {
    code: 'Dr',
    name: COND_NAMES.Dr,
    category: 'status',
    hasDuration: false,
    requiresLocation: false,
    vehicleOnly: false,
  },
  {
    // Regeneration halted by Suppressing(X) (v8.286.0) - GM-settable
    // too, for rulings ("the promethium is still burning in the wound").
    code: 'Rg',
    name: COND_NAMES.Rg,
    category: 'status',
    hasDuration: true,
    requiresLocation: false,
    vehicleOnly: false,
  },
  {
    // Guard beaten aside by Disarming(X) (v8.287.0) - parry suspended.
    code: 'Dsm',
    name: COND_NAMES.Dsm,
    category: 'status',
    hasDuration: true,
    requiresLocation: false,
    vehicleOnly: false,
  },
  {
    code: 'Hw',
    name: COND_NAMES.Hw,
    category: 'status',
    hasDuration: true,
    // The number is the five-row table's severity index (v8.199.8) -
    // 1 Minor Disruption … 4 Total Shutdown - decaying one row per round.
    durationUnit: 'severity',
    requiresLocation: false,
    vehicleOnly: false,
  },
  {
    // Hallucinating (v8.357.0): the number is an INTENSITY 1-10 in six
    // bands - the picker offers a figure or a roll, stepping or holding.
    code: 'Hl',
    name: COND_NAMES.Hl,
    category: 'status',
    hasDuration: true,
    durationUnit: 'intensity',
    requiresLocation: false,
    vehicleOnly: false,
    bodyOnly: true,
  },

  // Location-specific
  {
    code: 'Cr',
    name: COND_NAMES.Cr,
    category: 'location',
    hasDuration: false,
    requiresLocation: true,
    vehicleOnly: false,
  },
  {
    code: 'De',
    name: COND_NAMES.De,
    category: 'location',
    hasDuration: false,
    requiresLocation: true,
    vehicleOnly: false,
  },

  // IMMOBILISED IS NOT A VEHICLE CONDITION. The rules spec lists it among
  // the general ones - "cannot move or Dodge, −30 WS" - and a person has
  // been able to acquire it in play all along, because a weapon that
  // snares somebody stores it (weaponEffects.ts). Marking it vehicle-only
  // meant the GM could not apply by hand a condition the engine was
  // applying by itself.
  {
    code: 'Immob',
    name: COND_NAMES.Immob,
    category: 'debilitating',
    hasDuration: false,
    requiresLocation: false,
    vehicleOnly: false,
  },
  // Snared (v8.198.2): the weapon-applied Immobilised. Pickable so a GM
  // can hand it out or clear it; distinct from Immob so only THIS one
  // carries the per-turn escape test.
  {
    code: 'Sn',
    name: COND_NAMES.Sn,
    category: 'debilitating',
    hasDuration: false,
    requiresLocation: false,
    vehicleOnly: false,
  },
  // Pinned (v8.198.4): suppression. Shaken off by the per-turn WP test.
  {
    code: 'Pn',
    name: COND_NAMES.Pn,
    category: 'debilitating',
    hasDuration: false,
    requiresLocation: false,
    vehicleOnly: false,
  },

  // Vehicle-specific
  {
    code: 'WepDest',
    name: COND_NAMES.WepDest,
    category: 'vehicle',
    hasDuration: false,
    requiresLocation: false,
    vehicleOnly: true,
  },
  {
    code: 'EngDmg',
    name: COND_NAMES.EngDmg,
    category: 'vehicle',
    hasDuration: false,
    requiresLocation: false,
    vehicleOnly: true,
  },
  {
    code: 'HullBr',
    name: COND_NAMES.HullBr,
    category: 'vehicle',
    hasDuration: false,
    requiresLocation: false,
    vehicleOnly: true,
  },
  {
    code: 'CrewHit',
    name: COND_NAMES.CrewHit,
    category: 'vehicle',
    hasDuration: false,
    requiresLocation: false,
    vehicleOnly: true,
  },
  {
    code: 'Disabled',
    name: COND_NAMES.Disabled,
    category: 'vehicle',
    hasDuration: false,
    requiresLocation: false,
    vehicleOnly: true,
  },
];

// ── Category metadata ─────────────────────────────────────────

export const CATEGORY_LABELS: Record<ConditionCategory, string> = {
  fatal: 'Fatal',
  debilitating: 'Debilitating',
  status: 'Status',
  location: 'Location-Specific',
  vehicle: 'Vehicle',
};

export const CATEGORY_ORDER: ConditionCategory[] = ['fatal', 'debilitating', 'status', 'location', 'vehicle'];

// ── Immunity Map ──────────────────────────────────────────────

/**
 * Consolidated immunity lookup - unifies the scattered immunity
 * checks from postHitEffects.ts and traitCatalog.ts.
 *
 * Each entry maps a target trait field to the condition base codes
 * it grants immunity to.
 */
export const IMMUNITY_MAP: ImmunitySource[] = [
  // The book: Fear, Pinning, Insanity and the mind clouded or deluded (ruling 2026-10-11) - Fear is the resistance
  // engine's, so here Pinned and Hallucinating. Not Stunned: no source the user accepts lists it.
  { field: 'fromBeyond', label: 'From Beyond', conditions: ['Pn', 'Hl'], isNumeric: false },
  { field: 'blind', label: 'Blind', conditions: ['B'], isNumeric: false },
  { field: 'machineAP', label: 'Machine', conditions: ['S', 'BL', 'BLS', 'Fi', 'Pn'], isNumeric: true },
  {
    field: 'stuffOfNightmares',
    label: 'Stuff of Nightmares',
    conditions: ['BL', 'BLS', 'S', 'F', 'Pn'],
    isNumeric: false,
  },
  { field: 'astartes', label: 'Astartes', conditions: ['BL', 'BLS'], isNumeric: false },
  { field: 'hardy', label: 'Hardy', conditions: ['BL', 'BLS'], isNumeric: false },
  { field: 'frenzied', label: 'Frenzied', conditions: ['S', 'F', 'Pn'], isNumeric: false },
  { field: 'undying', label: 'Undying', conditions: ['K', 'Dy'], isNumeric: false },
];

// ── Helpers ───────────────────────────────────────────────────

/** Check whether a trait is active on a target. */
function isTraitActive(target: Record<string, unknown>, source: ImmunitySource): boolean {
  const val = target[source.field];
  if (source.isNumeric) return typeof val === 'number' && val > 0;
  return !!val;
}

/**
 * Extract the base condition letter from a code, handling duration
 * variants like S3, B2, F1, Dy1. Returns the code itself for
 * multi-letter codes (BL, BLS, Fi, etc.).
 */
function condBaseCode(code: string): string {
  // Location-specific: Cr:Head → Cr, De:Left Arm → De
  const locMatch = code.match(/^(Cr|De):/);
  if (locMatch) return locMatch[1];

  // Hallucinating: Hl7 → Hl, Hl7:3 → Hl (v8.357.0)
  if (/^Hl\d+(?::\d+)?$/.test(code)) return 'Hl';
  // Duration variants: S3 → S, B2 → B, Dy1 → Dy, Hw3 → Hw
  const durMatch = code.match(/^([A-Z][a-z]*)\d+$/);
  if (durMatch) return durMatch[1];

  return code;
}

// ── Core Functions ────────────────────────────────────────────

/**
 * Is this target immune to a condition, by any trait?
 *
 * The single source of truth, used by the modifier layer, the per-turn
 * effects, and the paths that APPLY conditions. Immunity has to be
 * checked in all three: marking a condition immune in the picker does
 * not stop a critical table or a Flame hit from adding it, and it does
 * not stop an already-present one from taking effect.
 *
 * `base` is a bare code - strip any duration first (`S3` → `S`).
 */
export function isImmuneTo(target: Record<string, unknown>, base: string): boolean {
  // An object takes no condition but its destruction (2026-10-26): no fire, no fear, no stun - it is a thing. K is
  // left open so the GM may mark it destroyed by hand, as the attack path does (postHitEffects isConditionImmune).
  if (isObject(target as BodyTyped)) return !objectTakes(base);
  return IMMUNITY_MAP.some((src) => src.conditions.includes(base) && isTraitActive(target, src));
}

/** The labels of the active traits that make the target immune to `code` (a bare code). */
export function immuneSourcesFor(target: Record<string, unknown>, code: string): string[] {
  // An Object (2026-10-26) is immune to every condition by what it is, not by a trait.
  if (isObject(target as BodyTyped)) return objectTakes(code) ? [] : ['Object'];
  return IMMUNITY_MAP.filter((src) => isTraitActive(target, src) && src.conditions.includes(code)).map(
    (src) => src.label,
  );
}

/**
 * Get the list of conditions eligible for a target, annotated with
 * immunity information. Filters by body type:
 * - Vehicle-only conditions hidden for non-vehicle targets
 * - Body-only conditions hidden for vehicles, alone or a crowd
 * - Location-specific conditions hidden for bodies with no locations
 *
 * A location-scoped condition on a body with no locations can only ever
 * be stored as a bare code, and a bare `Cr` expands to NOTHING - it shows
 * as active on the sheet and imposes nothing, for ever. Amorphous things
 * and hordes have no limbs to cripple, so the honest answer is not to
 * offer it rather than to invent a rule for it.
 */

export function getEligibleConditions(target: Record<string, unknown>): EligibleCondition[] {
  const bodyType = (target.bodyType as BodyTypeId) || 'humanoid';
  const isVehicle = bodyType === 'vehicle';
  const hasLocations = getLocationOptions(bodyType).length > 0;
  const fits = (entry: ConditionEntry) =>
    (isVehicle ? !entry.bodyOnly : !entry.vehicleOnly) && (hasLocations || !entry.requiresLocation);
  return CONDITION_CATALOG.filter(fits).map((entry) => {
    const immuneSources = immuneSourcesFor(target, entry.code);
    return { ...entry, immune: immuneSources.length > 0, immuneSources };
  });
}

/**
 * Get the hit location options for Crippled/Destroyed location picker.
 * Returns location names appropriate for the body type.
 */
export function getLocationOptions(bodyType: BodyTypeId | undefined): LocationOption[] {
  const config = getBodyConfig(bodyType);
  if (!config.supportsConditionLocations) return [];
  return config.locations.map((loc) => ({ id: loc.id, label: loc.name }));
}

/**
 * Compute the result of toggling a trait ON - determines which active
 * conditions should be removed due to the trait's immunity.
 *
 * Pure function: does not mutate the input array.
 */
export function computeTraitCleanup(conditions: string[], traitField: string, active: boolean): TraitCleanupResult {
  const source = IMMUNITY_MAP.find((s) => s.field === traitField);

  // No immunity data for this trait, or toggling OFF → no-op
  if (!source || !active) {
    return {
      cleaned: [...conditions],
      removed: [],
      traitLabel: source?.label || traitField,
    };
  }

  const immuneSet = new Set(source.conditions);
  const isImmune = (cond: string) => immuneSet.has(condBaseCode(cond));
  const cleaned = conditions.filter((cond) => !isImmune(cond));
  const removed = conditions
    .filter(isImmune)
    .map(decodeCond)
    .filter((decoded): decoded is string => !!decoded);
  return { cleaned, removed, traitLabel: source.label };
}
